import type { MarketCountryCode } from '@backmarket/http-api'
import type {
  PaymentError,
  PaymentGroup,
  PaymentMethod,
  PaymentMethodConfig,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import type { UnionOfPropertyTypes } from '@backmarket/utils/object/UnionOfPropertyTypes'

import type { MaybeI18nDefinition } from '../../../utils/maybeI18nDefinition'
import type { PaymentFormVersion } from '../../form-common/composables/usePaymentFormVersion'

export const LocalPaymentMethodReference = {
  LEGACY_CARD: 'LEGACY_CARD',
  SEPA: 'SEPA',
  BACS: 'BACS',
  ACH: 'ACH',
} as const

export type LocalPaymentMethodReference = UnionOfPropertyTypes<
  typeof LocalPaymentMethodReference
>

/**
 * Some payment methods are hardcoded in the application
 * and are not retrieved from the API. They are `Local`...
 *
 * (this type is only used for buyback methods -
 * don't use it directly and use the hardcoded buyback methods instead)
 */
export type LocalPaymentMethod = {
  isLocal: true
  reference: LocalPaymentMethodReference
}

export type PaymentMethodHardcodedConfigBase = {
  /**
   * Payment method name
   */
  name: MaybeI18nDefinition

  /**
   * Option label in payment page
   */
  label?: I18nDefinition

  /**
   * A function, that, if present, will be called in the browser,
   * to determine whether the payment method is available.
   */
  isAvailableInBrowser?: (config: PaymentMethod) => Promise<boolean>
}

export type PaymentMethodWithContextHardcodedConfig<T> = Omit<
  PaymentMethodHardcodedConfigBase,
  'isAvailableInBrowser'
> & {
  /**
   * A function, that, if present, will be called on setup hook
   * to initialize the payment method context.
   */
  useAvailabilityContext: (config: PaymentMethod) => T

  /**
   * Override the default availability check, add availability context
   */
  isAvailableInBrowser?: (
    config: PaymentMethodConfig,
    context?: T,
  ) => Promise<boolean>
}

export type PaymentMethodWithInstallmentHardcodedConfig =
  PaymentMethodHardcodedConfigBase & {
    installmentCount: number
    ratesByCountry?: Partial<Record<MarketCountryCode, string>>
  }

export type PaymentMethodHardcodedConfig<T = unknown> =
  | PaymentMethodHardcodedConfigBase
  | PaymentMethodWithContextHardcodedConfig<T>
  | PaymentMethodWithInstallmentHardcodedConfig

export type PaymentGroupHardcodedConfig = {
  label: MaybeI18nDefinition

  /**
   * Whether the group has a custom icon
   * `true` will expect an icon in
   * (/img/payment/groups-v2/), while `false` will re-use either network icons
   * (/img/payment/networks-v2/), or methods icons (/img/payment/methods-v2/).
   */
  hasCustomIcon: boolean

  // TODO: check if we can remove this
  external?: boolean

  /**
   * Messages to display in the payment page
   */
  messages: I18nDefinition[]

  providerName: string | I18nDefinition

  errorMessages?: Partial<Record<PaymentError, I18nDefinition>>

  legalNotices?: Partial<
    Record<
      MarketCountryCode,
      {
        definition: I18nDefinition
        links: Record<string, { definition: I18nDefinition; url: string }>
        values: Record<string, unknown>
      }
    >
  >
}

export type PaymentGroupDynamicHardcodedConfigOptions = {
  methods: PaymentMethod[]
  // TODO [PAYIN-4199]: remove this during AB test cleanup: https://backmarket.atlassian.net/browse/PAYIN-4199
  formVersion: PaymentFormVersion
}

export type PaymentNetworkHardcodedConfig = {
  name: MaybeI18nDefinition
}

export type PaymentGroupData = {
  id: PaymentGroup
  enabledMethods: PaymentMethod[]
  hasManyEnabledMethods: boolean
  hasMessages: boolean
  isEnabled: boolean
  label: MaybeI18nDefinition
  methods: PaymentMethod[]
  config: PaymentGroupHardcodedConfig
}
